import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { ArrowRight, ShieldCheck, AlertTriangle } from "lucide-react";
import { PlatformShellHeader, PlatformShellFooter, useSeo } from "./PlatformShell";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MODELS = ["BBC Pro", "BBC HDS", "BBC Flash", "Not sure — recommend"];
const SITE_TYPES = [
  "Residential society",
  "Commercial / office entry",
  "Industrial site / factory",
  "Toll plaza / FASTag lane",
  "Corporate park",
  "Hospital / hotel / mall parking",
  "Other",
];
const ACCESS_TYPES = [
  "RFID stickers",
  "ANPR cameras",
  "FASTag",
  "Manual (remote / push button)",
  "Mix / not decided",
];

export default function Quote() {
  const { search } = useLocation();
  const presetModel = useMemo(() => {
    const m = new URLSearchParams(search).get("model");
    return MODELS.includes(m) ? m : MODELS[0];
  }, [search]);

  const [form, setForm] = useState({
    name: "", email: "", phone: "", company: "",
    site_type: SITE_TYPES[0], model: presetModel, access: ACCESS_TYPES[0],
    units: "1–5", site_location: "", timeline: "Within 30 days", message: "",
  });
  const [status, setStatus] = useState("idle");
  const [err, setErr] = useState("");
  const [refId, setRefId] = useState("");

  // Keep the dropdown in sync if the URL param changes after mount
  useEffect(() => {
    setForm((f) => ({ ...f, model: presetModel }));
  }, [presetModel]);

  useSeo({
    title: presetModel && presetModel !== MODELS[3] && presetModel !== "BBC Pro"
      ? `${presetModel} — Request a Quote | Boom Barriers, Gate Automation | Boom Barrier Company`
      : "Request a Quote — Boom Barriers, Gate Automation & RFID/ANPR | Boom Barrier Company",
    description: `Request a site-specific quote for ${presetModel === MODELS[3] ? "BBC automatic boom barriers" : presetModel} in India. BBC Pro · HDS · Flash. RFID, ANPR and FASTag integration. Response within one business day.`,
    canonical: "https://www.boombarriercompany.com/quote",
    image: "https://www.boombarriercompany.com/branded/bbc-pro.png",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "ContactPage",
      "name": "Request a Quote — Boom Barrier Company",
      "url": "https://www.boombarriercompany.com/quote",
    },
  });

  useEffect(() => {
    if (status !== "success") return;
    setRefId(`BBC-${Math.random().toString(36).slice(2, 8).toUpperCase()}`);
  }, [status]);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus("submitting"); setErr("");
    const msg = [
      `Site: ${form.site_type}`,
      `Preferred Model: ${form.model}`,
      `Access: ${form.access}`,
      `Units: ${form.units}`,
      `Location: ${form.site_location}`,
      `Timeline: ${form.timeline}`,
      form.message && `Notes: ${form.message}`,
    ].filter(Boolean).join("\n");
    try {
      await axios.post(`${API}/leads`, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        company: form.company.trim() || null,
        message: msg,
        type: "quote",
        source: "quote-page",
      });
      setStatus("success");
    } catch (e2) {
      const d = e2?.response?.data?.detail;
      setErr(typeof d === "string" ? d : "Could not submit. Please check the form and try again.");
      setStatus("error");
    }
  };

  return (
    <div data-testid="quote-root" className="min-h-screen bg-[#0F1113] text-white font-body antialiased">
      <PlatformShellHeader />

      <section className="relative pt-20 md:pt-28 pb-16 border-b border-white/5">
        <div className="absolute inset-0 bbc-grid-bg opacity-[0.06] pointer-events-none" />
        <div className="relative max-w-5xl mx-auto px-6 md:px-12">
          <div className="flex items-center gap-3">
            <span className="w-8 h-px bg-[#00FF85]" />
            <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#00FF85]">/// — Request a Quote</span>
          </div>
          <h1 className="font-display mt-6 text-white text-4xl md:text-6xl lg:text-7xl font-black tracking-[-0.02em] leading-[0.98]">
            Get a site-specific
            <br />
            <span className="text-[#00FF85]">boom barrier quote.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-[#A1A1AA] text-base md:text-lg font-body leading-relaxed">
            Tell us about your site — traffic, access control, units, timeline. We'll spec the right BBC model (Pro / HDS / Flash) and the right cabinet, and respond within one business day. Indiamart, Justdial and TradeIndia leads can be routed to this form via webhook on request.
          </p>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          {status === "success" ? (
            <div data-testid="quote-success" className="border border-[#00FF85]/40 bg-[#00FF85]/[0.06] p-10 text-center">
              <div className="mx-auto w-14 h-14 border-2 border-[#00FF85] flex items-center justify-center mb-5">
                <ShieldCheck className="text-[#00FF85]" size={26} />
              </div>
              <h2 className="font-display text-white text-3xl font-black tracking-tight">Logged. Thank you.</h2>
              <p className="mt-3 text-[#A1A1AA] max-w-md mx-auto">
                Your enquiry has been recorded against ref <span className="text-[#00FF85] font-mono">{refId}</span>. A BBC engineer will reach out within one business day.
              </p>
            </div>
          ) : (
            <form onSubmit={submit} className="border border-white/10 bg-[#16191C] p-7 md:p-10 space-y-5" data-testid="quote-form">
              <div className="grid sm:grid-cols-2 gap-5">
                <Input label="Full Name *" required value={form.name} onChange={onChange("name")} testId="quote-name" placeholder="Rajesh Kumar" />
                <Input label="Company / Society" value={form.company} onChange={onChange("company")} testId="quote-company" placeholder="Optional" />
                <Input label="Email *" type="email" required value={form.email} onChange={onChange("email")} testId="quote-email" placeholder="rajesh@firm.in" />
                <Input label="Phone *" type="tel" required value={form.phone} onChange={onChange("phone")} testId="quote-phone" placeholder="+91 98xxxxxxxx" />
              </div>

              <div className="grid sm:grid-cols-2 gap-5">
                <Select label="Site Type *" value={form.site_type} onChange={onChange("site_type")} options={SITE_TYPES} testId="quote-site-type" />
                <Select label="Preferred Model" value={form.model} onChange={onChange("model")} options={MODELS} testId="quote-model" />
                <Select label="Access Control" value={form.access} onChange={onChange("access")} options={ACCESS_TYPES} testId="quote-access" />
                <Select label="Number of Barriers" value={form.units} onChange={onChange("units")} options={["1–5", "6–20", "21–50", "50+"]} testId="quote-units" />
              </div>

              <Input label="Site Location (city / state)" value={form.site_location} onChange={onChange("site_location")} testId="quote-location" placeholder="e.g. Whitefield, Bengaluru" />
              <Select label="Timeline" value={form.timeline} onChange={onChange("timeline")} options={["Within 30 days", "1–3 months", "3–6 months", "Exploring"]} testId="quote-timeline" />

              <label className="block">
                <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#A1A1AA]">Anything else?</span>
                <textarea
                  rows={3}
                  value={form.message}
                  onChange={onChange("message")}
                  data-testid="quote-message"
                  placeholder="e.g. 8m gate, monsoon-prone area, existing FASTag readers."
                  className="mt-1.5 w-full bg-[#0F1113] border border-white/10 focus:border-[#00FF85] outline-none px-3 py-3 text-white text-sm font-body transition resize-none"
                />
              </label>

              {status === "error" && (
                <div className="flex items-start gap-2 border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-300" data-testid="quote-error">
                  <AlertTriangle size={16} className="mt-0.5" />
                  <span>{err || "Could not submit. Please try again."}</span>
                </div>
              )}

              <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
                <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#52525B] max-w-md">
                  By submitting you agree to be contacted by BBC for this enquiry. Response within one business day.
                </p>
                <button
                  type="submit"
                  disabled={status === "submitting"}
                  data-testid="quote-submit"
                  className="inline-flex items-center gap-3 bg-[#00FF85] hover:bg-[#00CC6A] disabled:opacity-60 text-black font-semibold px-7 py-4 text-xs uppercase tracking-wider transition shadow-[0_0_28px_rgba(0,255,133,0.4)]"
                >
                  {status === "submitting" ? "Submitting…" : "Submit Quote Request"}
                  <ArrowRight size={14} />
                </button>
              </div>
            </form>
          )}
        </div>
      </section>

      <PlatformShellFooter />
    </div>
  );
}

const Input = ({ label, required, value, onChange, type = "text", placeholder, testId }) => (
  <label className="block">
    <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#A1A1AA]">
      {label}{required && <span className="text-[#00FF85]"> *</span>}
    </span>
    <input
      type={type}
      required={required}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      data-testid={testId}
      className="mt-1.5 w-full bg-[#0F1113] border border-white/10 focus:border-[#00FF85] outline-none px-3 py-3 text-white text-sm font-body transition"
    />
  </label>
);

const Select = ({ label, value, onChange, options, testId }) => (
  <label className="block">
    <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#A1A1AA]">{label}</span>
    <select
      value={value}
      onChange={onChange}
      data-testid={testId}
      className="mt-1.5 w-full bg-[#0F1113] border border-white/10 focus:border-[#00FF85] outline-none px-3 py-3 text-white text-sm font-body transition appearance-none cursor-pointer"
    >
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  </label>
);
